import React, { useEffect, useMemo, useState } from 'react';
import { Bell, Send } from 'lucide-react';

import API from '../../../util/API';
import AdminPageShell from '../components/AdminPageShell';

const PUSH_TARGET_ALL = 'all_users';
const PUSH_TARGET_CUSTOM = 'custom_selected';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Input = ({ className, ...props }) => (
  <input
    {...props}
    className={cx(
      'w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900',
      'focus:outline-none focus:ring-2 focus:ring-[#D2042D]/25 focus:border-[#D2042D]',
      className
    )}
  />
);

const Select = ({ className, ...props }) => (
  <select
    {...props}
    className={cx(
      'w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900',
      'focus:outline-none focus:ring-2 focus:ring-[#D2042D]/25 focus:border-[#D2042D]',
      className
    )}
  />
);

export default function PushNotificationPage() {
  const [title, setTitle] = useState('New Update');
  const [message, setMessage] = useState('Version 2.0 is now available');
  const [page, setPage] = useState('');
  const [targetMode, setTargetMode] = useState(PUSH_TARGET_ALL);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [pushStats, setPushStats] = useState(null);

  const loadPushStats = async () => {
    setStatsLoading(true);
    try {
      const response = await API.get('/notification/push/stats');
      setPushStats(response?.data?.data || null);
    } catch {
      setPushStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await API.get('/user');
        const list = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (!mounted) return;
        setUsers(list.filter((user) => String(user?.role || '').toLowerCase() !== 'admin'));
      } catch (requestError) {
        if (!mounted) return;
        setUsers([]);
        setError(
          requestError?.response?.data?.error?.message ||
            requestError?.response?.data?.message ||
            requestError?.message ||
            'Failed to load users'
        );
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    };

    loadUsers();
    loadPushStats();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedUsersCount = selectedUserIds.length;

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        label:
          user?.name ||
          [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
          user?.username ||
          user?.email ||
          String(user?._id || ''),
        value: String(user?._id || ''),
      })),
    [users]
  );

  const handleUserSelection = (event) => {
    const values = Array.from(event.target.selectedOptions || []).map((option) => option.value);
    setSelectedUserIds(values);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setResult(null);

    const trimmedTitle = String(title || '').trim();
    const trimmedMessage = String(message || '').trim();
    const trimmedPage = String(page || '').trim();

    if (!trimmedTitle) {
      setError('Title is required');
      return;
    }
    if (!trimmedMessage) {
      setError('Message is required');
      return;
    }
    if (targetMode === PUSH_TARGET_CUSTOM && selectedUsersCount === 0) {
      setError('Please select at least one user for custom-selected sending');
      return;
    }

    try {
      setSending(true);
      const payload = {
        title: trimmedTitle,
        message: trimmedMessage,
        audience: targetMode,
      };
      if (trimmedPage) payload.page = trimmedPage;
      if (targetMode === PUSH_TARGET_CUSTOM) payload.userIds = selectedUserIds;

      const response = await API.post('/notification/push/send', payload);
      const responseData = response?.data || {};
      setResult(responseData);
      loadPushStats();
      if (targetMode === PUSH_TARGET_CUSTOM) {
        setSelectedUserIds([]);
      }
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error?.message ||
          requestError?.response?.data?.message ||
          requestError?.message ||
          'Failed to send push notification'
      );
    } finally {
      setSending(false);
    }
  };

  const chips = (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-800">
        <Bell className="h-3.5 w-3.5" />
        {loadingUsers ? 'Loading users...' : `${users.length} app users`}
      </span>
      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-700">
        Target: {targetMode === PUSH_TARGET_ALL ? 'All User' : 'Custom-Selected'}
      </span>
      <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-700">
        Tokens: {statsLoading ? '...' : String(pushStats?.totalTokens ?? 0)}
      </span>
    </div>
  );

  return (
    <AdminPageShell
      title="Push Notification"
      description="Send app push notifications to all users or selected users."
      chips={chips}
    >
      <form onSubmit={handleSubmit} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-black uppercase tracking-[0.16em] text-slate-500 mb-2">
              Title
            </label>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="New Update"
              maxLength={120}
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-xs font-black uppercase tracking-[0.16em] text-slate-500 mb-2">
              Message
            </label>
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Version 2.0 is now available"
              rows={4}
              maxLength={500}
              className={cx(
                'w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900',
                'focus:outline-none focus:ring-2 focus:ring-[#D2042D]/25 focus:border-[#D2042D]'
              )}
              required
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-[0.16em] text-slate-500 mb-2">
              Page (Optional)
            </label>
            <Input
              value={page}
              onChange={(event) => setPage(event.target.value)}
              placeholder="/cart or /order/123"
            />
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-[0.16em] text-slate-500 mb-2">
              Users
            </label>
            <Select
              value={targetMode}
              onChange={(event) => {
                setTargetMode(event.target.value);
                setSelectedUserIds([]);
              }}
            >
              <option value={PUSH_TARGET_ALL}>All User</option>
              <option value={PUSH_TARGET_CUSTOM}>Custom-Selected</option>
            </Select>
          </div>

          {targetMode === PUSH_TARGET_CUSTOM ? (
            <div className="md:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.16em] text-slate-500 mb-2">
                Custom User Select
              </label>
              <Select
                multiple
                value={selectedUserIds}
                onChange={handleUserSelection}
                className="min-h-[220px]"
              >
                {userOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <p className="mt-2 text-xs text-slate-500">
                Hold Ctrl (Windows) or Command (Mac) for multi-select. Selected: {selectedUsersCount}
              </p>
            </div>
          ) : null}
        </div>

        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {result?.success ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {result?.message || 'Push notification request processed.'}
          </div>
        ) : null}

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          Push readiness: {statsLoading ? 'Loading...' : `${pushStats?.usersWithTokens ?? 0}/${pushStats?.totalUsers ?? 0} users have tokens`}
        </div>

        <div className="mt-5 flex items-center justify-end">
          <button
            type="submit"
            disabled={sending || loadingUsers}
            className={cx(
              'inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold transition-colors',
              sending || loadingUsers
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-[#D2042D] text-white hover:bg-[#b80327]'
            )}
          >
            <Send className="h-4 w-4" />
            {sending ? 'Sending...' : 'Send Push Notification'}
          </button>
        </div>
      </form>
    </AdminPageShell>
  );
}
